import axios, {AxiosPromise, ResponseType} from 'axios';
import qs from 'qs';

type Props = {
  method: 'GET' | 'PUT' | 'POST' | 'DELETE';
  url: string;
  headers?: Record<string, any>;
  data?: Record<string, any>;
  params?: Record<string, any>;
  responseType?: ResponseType;
};

const isURLEncoded = (headers: Record<string, any> = {}) =>
  Object.entries(headers).some(
    ([key, value]) => /^content-type$/i.test(key) && value === 'application/x-www-form-urlencoded',
  );

export type CancellableRequest<T> = AxiosPromise<T> & {cancel: () => void; isCancelled: boolean};

/**
 * An Axios wrapper. Takes method, url, headers, data, params and will send http request.
 * The returned promise comes with a `cancel` method to cancel the request.
 *
 * Takes a type T representing the type of the body of the response
 */
export const makeRequest = <T = any>({method, url, headers, data, params, responseType}: Props) => {
  const controller = new AbortController();
  const request = axios({
    method,
    url,
    headers,
    data: data && isURLEncoded(headers) ? qs.stringify(data) : data,
    params,
    signal: controller.signal,
    responseType,
  }) as CancellableRequest<T>;

  request.isCancelled = false;
  // Attach a cancel function to the request so callers can cancel the axios request
  request.cancel = function () {
    controller.abort();
    this.isCancelled = true;
  };

  return request;
};
